import { Trash2 } from "lucide-react";
import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { Button } from "~/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { useUserDisplayName } from "~/hooks/useUserDisplayName";
import { cn } from "~/lib/utils";
import {
  ANNOTATION_COLOR_LIST,
  ANNOTATION_ICON_NAMES,
  DEFAULT_ANNOTATION_COLOR,
  DEFAULT_ANNOTATION_ICON,
  type PageAnnotation,
} from "../types";
import {
  getAnnotationIcon,
  getIconContrastColor,
} from "../utils/annotationIcons";

interface StickyNoteOverlayProps {
  annotation: PageAnnotation;
  isExpanded: boolean;
  onToggleExpand: (id: string) => void;
  onContentChange: (id: string, content: string) => void;
  onColorChange: (id: string, color: string) => void;
  onIconChange: (id: string, icon: string) => void;
  onTitleChange?: (id: string, title: string) => void;
  onPositionChange?: (id: string, positionX: number, positionY: number) => void;
  onDelete: (id: string) => void;
  isViewOnly: boolean;
  rotation?: number;
  pageNumber?: number;
}

const COLLAPSED_SIZE = 28;
const DRAG_THRESHOLD = 4;

const StickyNoteOverlayComponent: FC<StickyNoteOverlayProps> = ({
  annotation,
  isExpanded,
  onToggleExpand,
  onContentChange,
  onColorChange,
  onIconChange,
  onTitleChange,
  onPositionChange,
  onDelete,
  isViewOnly,
  rotation = 0,
  pageNumber,
}) => {
  const intl = useIntl();
  const { getDisplayName } = useUserDisplayName();

  const defaultTitle = pageNumber
    ? intl.formatMessage(
        {
          id: "pageAnnotations.annotationPageTitle",
          defaultMessage: "Annotation - Page {number}",
        },
        { number: pageNumber }
      )
    : intl.formatMessage({
        id: "pageAnnotations.annotation",
        defaultMessage: "Annotation",
      });

  const displayPosition = useMemo(() => {
    const { positionX, positionY } = annotation;
    const normalizedRotation = ((rotation % 360) + 360) % 360;
    switch (normalizedRotation) {
      case 90:
        return { x: 1 - positionY, y: positionX };
      case 180:
        return { x: 1 - positionX, y: 1 - positionY };
      case 270:
        return { x: positionY, y: 1 - positionX };
      default:
        return { x: positionX, y: positionY };
    }
  }, [annotation.positionX, annotation.positionY, rotation]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [localContent, setLocalContent] = useState(annotation.content);
  const [localTitle, setLocalTitle] = useState(
    annotation.title || defaultTitle
  );
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const titleSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const lastSavedContentRef = useRef<string | null>(null);
  const lastSavedTitleRef = useRef<string | null>(null);
  const localContentRef = useRef(localContent);
  const localTitleRef = useRef(localTitle);
  const onContentChangeRef = useRef(onContentChange);
  const onTitleChangeRef = useRef(onTitleChange);
  const annotationIdRef = useRef(annotation.id);

  // Drag state for collapsed card repositioning
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const dragStartRef = useRef<{ mouseX: number; mouseY: number } | null>(null);
  const didDragRef = useRef(false);
  const dragCleanupRef = useRef<(() => void) | null>(null);

  // Drag state for expanded modal repositioning
  const [modalOffset, setModalOffset] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const modalDragRef = useRef<{
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const modalDragCleanupRef = useRef<(() => void) | null>(null);

  // Clear dragPosition once the server-side position catches up
  useEffect(() => {
    if (dragPosition && !isDragging) {
      const dx = Math.abs(displayPosition.x - dragPosition.x);
      const dy = Math.abs(displayPosition.y - dragPosition.y);
      if (dx < 0.01 && dy < 0.01) {
        setDragPosition(null);
      }
    }
  }, [displayPosition.x, displayPosition.y, dragPosition, isDragging]);

  // Cleanup drag listeners on unmount
  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
      modalDragCleanupRef.current?.();
    };
  }, []);

  // Reset modal offset when expanding/collapsing
  useEffect(() => {
    if (!isExpanded) {
      setModalOffset({ x: 0, y: 0 });
    }
  }, [isExpanded]);

  useEffect(() => {
    if (saveTimeoutRef.current) return;
    if (
      lastSavedContentRef.current !== null &&
      annotation.content !== lastSavedContentRef.current
    )
      return;
    setLocalContent(annotation.content);
    lastSavedContentRef.current = null;
  }, [annotation.content]);

  useEffect(() => {
    if (titleSaveTimeoutRef.current) return;
    if (
      lastSavedTitleRef.current !== null &&
      annotation.title !== lastSavedTitleRef.current
    )
      return;
    setLocalTitle(annotation.title || defaultTitle);
    lastSavedTitleRef.current = null;
  }, [annotation.title, defaultTitle]);

  useEffect(() => {
    localContentRef.current = localContent;
  }, [localContent]);

  useEffect(() => {
    localTitleRef.current = localTitle;
  }, [localTitle]);

  useEffect(() => {
    onContentChangeRef.current = onContentChange;
  }, [onContentChange]);

  useEffect(() => {
    onTitleChangeRef.current = onTitleChange;
  }, [onTitleChange]);

  useEffect(() => {
    annotationIdRef.current = annotation.id;
  }, [annotation.id]);

  useEffect(() => {
    if (isExpanded && textareaRef.current && !isViewOnly) {
      textareaRef.current.focus();
    }
  }, [isExpanded, isViewOnly]);

  const handleContentSave = useCallback(
    (content: string) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        lastSavedContentRef.current = content;
        onContentChange(annotation.id, content);
        saveTimeoutRef.current = null;
      }, 800);
    },
    [annotation.id, onContentChange]
  );

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        onContentChangeRef.current(
          annotationIdRef.current,
          localContentRef.current
        );
      }
      if (titleSaveTimeoutRef.current) {
        clearTimeout(titleSaveTimeoutRef.current);
        onTitleChangeRef.current?.(
          annotationIdRef.current,
          localTitleRef.current
        );
      }
    };
  }, []);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setLocalContent(newContent);
    handleContentSave(newContent);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setLocalTitle(newTitle);
    if (onTitleChange) {
      if (titleSaveTimeoutRef.current) {
        clearTimeout(titleSaveTimeoutRef.current);
      }
      titleSaveTimeoutRef.current = setTimeout(() => {
        lastSavedTitleRef.current = newTitle;
        onTitleChange(annotation.id, newTitle);
        titleSaveTimeoutRef.current = null;
      }, 800);
    }
  };

  const handleCollapsedClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (didDragRef.current) return;
    onToggleExpand(annotation.id);
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
      onContentChange(annotation.id, localContent);
    }
    if (titleSaveTimeoutRef.current) {
      clearTimeout(titleSaveTimeoutRef.current);
      titleSaveTimeoutRef.current = null;
      if (onTitleChange) {
        onTitleChange(annotation.id, localTitle);
      }
    }
    onToggleExpand(annotation.id);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    if (titleSaveTimeoutRef.current) {
      clearTimeout(titleSaveTimeoutRef.current);
      titleSaveTimeoutRef.current = null;
    }
    onDelete(annotation.id);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleModalDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (
        (e.target as HTMLElement).closest(
          "input, textarea, button, [role='dialog']"
        )
      )
        return;
      e.preventDefault();
      e.stopPropagation();

      modalDragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        offsetX: modalOffset.x,
        offsetY: modalOffset.y,
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!modalDragRef.current) return;
        const dx = moveEvent.clientX - modalDragRef.current.startX;
        const dy = moveEvent.clientY - modalDragRef.current.startY;
        setModalOffset({
          x: modalDragRef.current.offsetX + dx,
          y: modalDragRef.current.offsetY + dy,
        });
      };

      const handleMouseUp = () => {
        modalDragRef.current = null;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        modalDragCleanupRef.current = null;
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      modalDragCleanupRef.current = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    },
    [modalOffset]
  );

  // Drag handlers for collapsed state repositioning
  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      didDragRef.current = false;
      if (isViewOnly || !onPositionChange) return;
      e.stopPropagation();
      e.preventDefault();
      dragStartRef.current = { mouseX: e.clientX, mouseY: e.clientY };

      const parentEl = (e.currentTarget as HTMLElement).parentElement;
      if (!parentEl) return;

      let latestDragPos: { x: number; y: number } | null = null;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!dragStartRef.current) return;

        const dx = moveEvent.clientX - dragStartRef.current.mouseX;
        const dy = moveEvent.clientY - dragStartRef.current.mouseY;

        if (
          !didDragRef.current &&
          Math.abs(dx) < DRAG_THRESHOLD &&
          Math.abs(dy) < DRAG_THRESHOLD
        ) {
          return;
        }

        didDragRef.current = true;
        setIsDragging(true);

        const parentRect = parentEl.getBoundingClientRect();
        const newX = (moveEvent.clientX - parentRect.left) / parentRect.width;
        const newY = (moveEvent.clientY - parentRect.top) / parentRect.height;

        latestDragPos = {
          x: Math.max(0.02, Math.min(0.98, newX)),
          y: Math.max(0.02, Math.min(0.98, newY)),
        };
        setDragPosition(latestDragPos);
      };

      const handleMouseUp = () => {
        cleanup();

        if (didDragRef.current && latestDragPos) {
          const normalizedRotation = ((rotation % 360) + 360) % 360;
          let storageX = latestDragPos.x;
          let storageY = latestDragPos.y;

          switch (normalizedRotation) {
            case 90:
              storageX = latestDragPos.y;
              storageY = 1 - latestDragPos.x;
              break;
            case 180:
              storageX = 1 - latestDragPos.x;
              storageY = 1 - latestDragPos.y;
              break;
            case 270:
              storageX = 1 - latestDragPos.y;
              storageY = latestDragPos.x;
              break;
          }

          onPositionChange(annotation.id, storageX, storageY);
          // Keep dragPosition visible until API response updates displayPosition
        } else {
          setDragPosition(null);
        }

        setIsDragging(false);
        dragStartRef.current = null;
      };

      const cleanup = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        dragCleanupRef.current = null;
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      dragCleanupRef.current = cleanup;
    },
    [isViewOnly, onPositionChange, annotation.id, rotation]
  );

  const noteColor = annotation.color || DEFAULT_ANNOTATION_COLOR;
  const noteIcon = annotation.icon || DEFAULT_ANNOTATION_ICON;
  const IconComponent = getAnnotationIcon(noteIcon);
  const iconContrastColor = getIconContrastColor(noteColor);
  const authorName = getDisplayName(annotation.authorId);
  const createdDate = new Date(annotation.createdAt).toLocaleDateString(
    intl.locale,
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
  );

  const currentPosition = dragPosition ?? displayPosition;

  if (!isExpanded) {
    return (
      <div
        className={cn(
          "absolute z-10",
          isDragging ? "cursor-grabbing" : "cursor-pointer"
        )}
        style={{
          left: `${currentPosition.x * 100}%`,
          top: `${currentPosition.y * 100}%`,
          transform: "translate(-50%, -50%)",
        }}
        onClick={handleCollapsedClick}
        onMouseDown={handleDragStart}
      >
        <div
          className={cn(
            "rounded border-2 border-dashed shadow-md transition-transform flex items-center justify-center",
            !isDragging && "hover:scale-110"
          )}
          style={{
            width: COLLAPSED_SIZE,
            height: COLLAPSED_SIZE,
            backgroundColor: noteColor,
            borderColor: `${noteColor}CC`,
          }}
        >
          <IconComponent
            className="h-3.5 w-3.5"
            style={{ color: iconContrastColor }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center"
      onClick={handleCloseClick}
    >
      <div
        className="w-64 rounded-lg shadow-lg border-2 bg-card overflow-hidden animate-in fade-in zoom-in-95 duration-150 cursor-grab active:cursor-grabbing"
        style={{
          borderColor: noteColor,
          transform: `translate(${modalOffset.x}px, ${modalOffset.y}px)`,
        }}
        onClick={handleOverlayClick}
        onMouseDown={handleModalDragStart}
      >
        <div
          className="flex items-center gap-1.5 px-3 py-2"
          style={{ backgroundColor: `${noteColor}30` }}
        >
          {!isViewOnly && (
            <IconSelector
              selectedIcon={noteIcon}
              onIconChange={(icon) => onIconChange(annotation.id, icon)}
            />
          )}
          <input
            type="text"
            value={localTitle}
            onChange={handleTitleChange}
            readOnly={isViewOnly}
            className={cn(
              "flex-1 text-xs font-medium text-foreground truncate bg-transparent",
              "border-0 outline-none focus:ring-0 p-0",
              !isViewOnly &&
                "hover:bg-background/30 focus:bg-background/30 rounded px-1 -mx-1"
            )}
            data-testid="annotation-title-input"
          />
        </div>

        <div className="px-3 py-1">
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <span>{authorName}</span>
            <span>&middot;</span>
            <span>{createdDate}</span>
          </div>
        </div>

        <div className="px-3 pb-2">
          <textarea
            ref={textareaRef}
            value={localContent}
            onChange={handleTextChange}
            readOnly={isViewOnly}
            placeholder={intl.formatMessage({
              id: "pageAnnotations.placeholder",
              defaultMessage: "Type a note...",
            })}
            className={cn(
              "w-full min-h-[60px] max-h-[120px] resize-none bg-transparent",
              "text-sm text-foreground placeholder:text-muted-foreground",
              "prose prose-sm overflow-y-auto",
              "border-0 outline-none focus:ring-0 p-0"
            )}
          />
        </div>

        {!isViewOnly && (
          <div className="flex items-center justify-between px-3 pb-2">
            <div className="flex items-center gap-1">
              {ANNOTATION_COLOR_LIST.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={cn(
                    "w-5 h-5 rounded-full border-2 transition-transform hover:scale-110",
                    color === noteColor
                      ? "border-foreground scale-110"
                      : "border-transparent"
                  )}
                  style={{ backgroundColor: color }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onColorChange(annotation.id, color);
                  }}
                />
              ))}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-destructive"
              onClick={handleDeleteClick}
              data-testid="annotation-delete-button"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        <div className="px-3 pb-2">
          <Button
            variant="secondary"
            size="sm"
            className="w-full text-xs"
            onClick={handleCloseClick}
            data-testid="annotation-save-close-button"
          >
            {intl.formatMessage({
              id: "pageAnnotations.saveAndClose",
              defaultMessage: "Save and Close",
            })}
          </Button>
        </div>
      </div>
    </div>
  );
};

interface IconSelectorProps {
  selectedIcon: string;
  onIconChange: (icon: string) => void;
}

const IconSelector: FC<IconSelectorProps> = ({
  selectedIcon,
  onIconChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const SelectedIcon = getAnnotationIcon(selectedIcon);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "h-5 w-5 flex items-center justify-center rounded transition-colors",
            isOpen
              ? "bg-background/50 text-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-background/30"
          )}
          onClick={(e) => e.stopPropagation()}
          data-testid="icon-selector-trigger"
        >
          <SelectedIcon className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={4}
        className="w-auto p-1.5 grid grid-cols-4 gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        {ANNOTATION_ICON_NAMES.map((iconName) => {
          const Icon = getAnnotationIcon(iconName);
          const isSelected = iconName === selectedIcon;
          return (
            <button
              key={iconName}
              type="button"
              className={cn(
                "w-7 h-7 rounded flex items-center justify-center transition-colors",
                isSelected
                  ? "bg-accent text-accent-foreground"
                  : "hover:bg-accent/50 text-muted-foreground hover:text-foreground"
              )}
              onClick={(e) => {
                e.stopPropagation();
                onIconChange(iconName);
                setIsOpen(false);
              }}
              title={iconName}
              data-testid={`icon-option-${iconName}`}
            >
              <Icon className="h-4 w-4" />
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
};

export const StickyNoteOverlay = memo(StickyNoteOverlayComponent);
StickyNoteOverlay.displayName = "StickyNoteOverlay";
